// RRI Initiative — the counties in the Rapid Results Initiative drive: RRI
// USAJILI figures (new IDs, ID replacements, voters registered, from the
// daily county returns) beside the voter roll for the same counties: voters
// the roll recorded over the RRI period, registered to date, ID holders not
// yet registered and coverage.

import React, { useMemo, useRef, useState } from 'react';
import { Box, Button, CircularProgress, Grid, LinearProgress, Typography } from '@mui/material';
import FlagIcon from '@mui/icons-material/Flag';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { useQuery } from '@tanstack/react-query';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { KenyaMap } from '@components/KenyaMap';
import { useUSAJILI } from '@hooks/useUSAJILI';
import { usajiliApi } from '@services/api/usajili';
import { registeredApi } from '@services/api/analytics';
import { exportPdf } from '@utils/pdfExport';
import { rriStatus } from '@components/usajili/usajiliPdf';
import { dayLabelLong } from './data';
import { emptyNrb, useIdRows } from './agg';
import { C, Column, DataTable, Dropdown, fmt, fmtShort, Kpi, Panel, Section } from './ui';

/** RRI county names ("Elgeyo Marakwet") as the roll / NRB spell them. */
const rollCounty = (c: string) => {
  const u = c.trim().toUpperCase();
  return u === 'ELGEYO MARAKWET' ? 'ELGEYO-MARAKWET' : u;
};

interface RriRow {
  key: string;
  county: string;
  newIds: number;
  replacements: number;
  rriVoters: number;
  rollInPeriod: number;
  registered: number;
  notRegistered: number;
  coverage: number;
}

/** county: the National overview county (roll spelling, '' = all). Picking a
 *  county here sets it, so the page and its PDF show just that county. */
export const RriSection: React.FC<{ county: string; onCounty: (c: string) => void }> = ({ county, onCounty }) => {
  const { metadata } = useUSAJILI();
  const from = metadata?.data_from || '';
  const to = metadata?.data_to || '';
  const rri = useQuery({
    queryKey: ['usajili', 'summary', from, to],
    queryFn: () => usajiliApi.summary({ date_from: from, date_to: to }),
    enabled: !!from,
    staleTime: 5 * 60 * 1000,
  });
  const roll = useIdRows(emptyNrb, { preset: 'all', from: '', to: '' });
  const inPeriod = useQuery({
    queryKey: ['analytics', 'reg-rows', 'rri-period', from, to],
    queryFn: () => registeredApi.rows({ county: '', constituency: '', ward: '', polling_centre: '', date_from: from, date_to: to }),
    enabled: !!from,
    staleTime: 5 * 60 * 1000,
  });

  const allRows: RriRow[] = useMemo(() => {
    const byRoll = new Map(roll.rows.map((r) => [r.name, r]));
    const byPeriod = new Map((inPeriod.data?.rows ?? []).map((r) => [r.name, r.registered]));
    return (rri.data?.rows ?? []).filter((r) => !r.is_grand_total).map((r) => {
      const k = rollCounty(r.county);
      const n = byRoll.get(k);
      return {
        key: k,
        county: r.county,
        newIds: r.new_ids,
        replacements: r.id_replacements,
        rriVoters: r.voters_registered,
        rollInPeriod: byPeriod.get(k) ?? 0,
        registered: n?.registered ?? 0,
        notRegistered: n?.notRegistered ?? 0,
        coverage: n?.pctRegistered ?? 0,
      };
    });
  }, [rri.data, roll.rows, inPeriod.data]);
  const rows = useMemo(() => (county ? allRows.filter((r) => r.key === county) : allRows), [allRows, county]);
  const countyOpts = useMemo(() => [...allRows].sort((a, b) => a.county.localeCompare(b.county)).map((r) => ({ value: r.key, label: r.county })), [allRows]);
  const notRri = !!county && allRows.length > 0 && rows.length === 0;
  const scopeLabel = county && rows.length ? `${rows[0].county} County` : `${allRows.length || 13} RRI counties`;

  const t = rows.reduce((a, r) => ({
    newIds: a.newIds + r.newIds, replacements: a.replacements + r.replacements, rriVoters: a.rriVoters + r.rriVoters,
    rollInPeriod: a.rollInPeriod + r.rollInPeriod, registered: a.registered + r.registered, notRegistered: a.notRegistered + r.notRegistered,
  }), { newIds: 0, replacements: 0, rriVoters: 0, rollInPeriod: 0, registered: 0, notRegistered: 0 });
  const coverage = t.registered + t.notRegistered ? (t.registered / (t.registered + t.notRegistered)) * 100 : 0;
  const loading = rri.isFetching || roll.fetching || inPeriod.isFetching;
  const period = from ? `${dayLabelLong(from)} to date` : 'to date';
  const mapRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<HTMLDivElement>(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState('');

  const columns: Column<RriRow>[] = [
    { key: 'county', label: 'County', value: (r) => r.county },
    { key: 'newIds', label: 'New IDs (RRI)', value: (r) => r.newIds, kind: 'num', color: C.blue },
    { key: 'replacements', label: 'ID replacements (RRI)', value: (r) => r.replacements, kind: 'num', color: C.cyan },
    { key: 'rriVoters', label: 'Voters registered (RRI)', value: (r) => r.rriVoters, kind: 'num', color: C.yellow },
    { key: 'rollInPeriod', label: 'On the roll, RRI period', value: (r) => r.rollInPeriod, kind: 'num', color: C.green },
    { key: 'registered', label: 'Registered to date', value: (r) => r.registered, kind: 'num', color: C.green },
    { key: 'notRegistered', label: 'ID holders not registered', value: (r) => r.notRegistered, kind: 'num', color: C.orange },
    {
      key: 'coverage', label: 'Coverage', value: (r) => r.coverage, kind: 'pct', total: () => coverage,
    },
  ];

  const onExport = async () => {
    setExporting(true);
    setExportError('');
    try {
      const n = (v: number) => fmt(v);
      await exportPdf({
        title: county ? `RRI Initiative · ${scopeLabel}` : 'RRI Initiative',
        status: rriStatus(metadata),
        subtitle: `Rapid Results Initiative · ${scopeLabel} · RRI returns ${period} · analytics.sdics.tech`,
        fileName: `rri-initiative-${county ? `${county.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-` : ''}${new Date().toISOString().slice(0, 10)}`,
        blocks: [
          { kind: 'kpis', items: [
            { label: 'New IDs (RRI)', value: n(t.newIds), sub: period, color: C.blue },
            { label: 'ID replacements (RRI)', value: n(t.replacements), sub: period, color: C.cyan },
            { label: 'Voters registered (RRI)', value: n(t.rriVoters), sub: 'county returns', color: C.yellow },
            { label: 'On the roll, RRI period', value: n(t.rollInPeriod), sub: 'IEBC roll, same counties and dates', color: C.green },
            { label: county ? 'Coverage' : 'Coverage, RRI counties', value: `${coverage.toFixed(1)}%`, sub: `${n(t.notRegistered)} ID holders not registered`, color: C.purple },
          ] },
          { kind: 'charts', height: 370, items: [
            { title: `${scopeLabel} · coverage`, el: mapRef.current, weight: 1, background: C.cardAlt },
            { title: 'Voters registered: RRI returns vs the voter roll, RRI period', el: chartRef.current, weight: 2,
              legend: [{ label: 'RRI returns', color: C.yellow }, { label: 'Voter roll', color: C.green }] },
          ] },
          { kind: 'pageBreak' },
          { kind: 'table', title: `Summary · ${scopeLabel}, ${period}`,
            columns: columns.map((c) => ({ label: c.label, align: c.kind ? 'right' as const : 'left' as const })),
            rows: [...rows].sort((a, b) => a.county.localeCompare(b.county)).map((r) => [
              r.county, n(r.newIds), n(r.replacements), n(r.rriVoters), n(r.rollInPeriod), n(r.registered), n(r.notRegistered), `${r.coverage.toFixed(1)}%`,
            ]),
            total: rows.length < 2 ? undefined : ['Total', n(t.newIds), n(t.replacements), n(t.rriVoters), n(t.rollInPeriod), n(t.registered), n(t.notRegistered), `${coverage.toFixed(1)}%`] },
          { kind: 'note', text: 'RRI figures are the daily county returns uploaded to the RRI USAJILI dashboard. Voter-roll figures are from the IEBC roll for the same counties: "On the roll, RRI period" counts voters whose registration date falls in the RRI period. Coverage is the share of ID holders registered.' },
        ],
      });
    } catch (e: any) {
      setExportError(e?.message || 'Could not create the PDF.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Section id="rri" icon={<FlagIcon />} title="RRI Initiative" accent={C.yellow}
      subtitle={`Rapid Results Initiative · ${scopeLabel} · RRI returns ${period}`}
      actions={
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <Dropdown label="County" value={notRri ? '' : county} options={countyOpts} onChange={onCounty}
          placeholder="All RRI counties" minWidth={190} hint={false} />
        <Button onClick={onExport} disabled={loading || exporting || !rows.length} variant="outlined"
          startIcon={exporting ? <CircularProgress size={16} sx={{ color: C.yellow }} /> : <PictureAsPdfIcon />}
          sx={{ borderColor: C.yellow, color: C.yellow, fontWeight: 700, '&:hover': { borderColor: C.yellow, backgroundColor: `${C.yellow}14` } }}>
          {exporting ? 'Exporting…' : 'Export PDF'}
        </Button>
        <Button href="https://rri.sdics.tech/usajili/dashboard" target="_blank" rel="noopener" variant="contained" endIcon={<OpenInNewIcon />}
          sx={{ backgroundColor: C.yellow, color: C.bg, fontWeight: 700, '&:hover': { backgroundColor: C.yellow, filter: 'brightness(1.1)' } }}>
          Open RRI dashboard
        </Button>
        </Box>
      }>
      {loading && <LinearProgress sx={{ height: 2, mb: 1, backgroundColor: 'transparent', '& .MuiLinearProgress-bar': { backgroundColor: C.yellow } }} />}
      {exportError && <Typography sx={{ color: C.orange, fontSize: '0.8rem', mb: 1 }}>{exportError}</Typography>}
      {notRri && (
        <Typography sx={{ color: C.orange, fontSize: '0.8rem', mb: 1 }}>
          The RRI drive does not cover this county; it runs in {allRows.length} counties. Pick one of them, or{' '}
          <Box component="span" onClick={() => onCounty('')} sx={{ textDecoration: 'underline', cursor: 'pointer', fontWeight: 700 }}>show all RRI counties</Box>.
        </Typography>
      )}
      {rri.error && <Typography sx={{ color: C.orange, fontSize: '0.8rem', mb: 1 }}>{(rri.error as any)?.message || 'Could not load the RRI returns.'}</Typography>}

      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={6} md={2.4}><Kpi label="New IDs (RRI)" value={t.newIds} color={C.blue} sub={period} /></Grid>
        <Grid item xs={6} md={2.4}><Kpi label="ID replacements (RRI)" value={t.replacements} color={C.cyan} sub={period} /></Grid>
        <Grid item xs={6} md={2.4}><Kpi label="Voters registered (RRI)" value={t.rriVoters} color={C.yellow} sub="county returns" /></Grid>
        <Grid item xs={6} md={2.4}><Kpi label="On the roll, RRI period" value={t.rollInPeriod} color={C.green} sub="IEBC roll, same counties and dates" /></Grid>
        <Grid item xs={12} md={2.4}> <Kpi label={county ? 'Coverage' : 'Coverage, RRI counties'} value={`${coverage.toFixed(1)}%`} color={C.purple} sub={`${fmt(t.notRegistered)} ID holders not registered`} /></Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}>
          <Panel title={`${scopeLabel} · coverage`} sx={{ height: 440 }}>
            <Box ref={mapRef} sx={{ height: 370, p: 1 }}>
              <KenyaMap background={C.cardAlt} labelColor={C.muted} countyData={rows.map((r) => ({
                name: r.key, lat: 0, lng: 0, registered: r.registered, unregistered: r.notRegistered, percentage: r.coverage,
              }))} />
            </Box>
            <Typography sx={{ fontSize: '0.65rem', color: C.muted, px: 1.5 }}>RRI counties coloured by share of ID holders registered; others grey</Typography>
          </Panel>
        </Grid>
        <Grid item xs={12} md={8}>
          <Panel title="Voters registered: RRI returns vs the voter roll, RRI period" sx={{ height: 440 }}>
            <Box ref={chartRef} sx={{ height: 380, p: 1 }}>
              <ResponsiveContainer>
                <BarChart data={[...rows].sort((a, b) => b.rriVoters - a.rriVoters).map((r) => ({ county: r.county, 'RRI returns': r.rriVoters, 'Voter roll': r.rollInPeriod }))}
                  margin={{ top: 8, right: 12, left: 0, bottom: 0 }} barGap={2}>
                  <CartesianGrid stroke={C.border} strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="county" tick={{ fill: C.muted, fontSize: 10 }} interval={0} angle={-30} textAnchor="end" height={60} />
                  <YAxis tickFormatter={fmtShort} tick={{ fill: C.muted, fontSize: 11 }} width={48} />
                  <Tooltip contentStyle={{ backgroundColor: C.card, border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 12 }}
                    cursor={{ fill: C.faint }} formatter={(v: number, n: string) => [v.toLocaleString(), n]} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="RRI returns" fill={C.yellow} radius={[3, 3, 0, 0]} maxBarSize={28} />
                  <Bar dataKey="Voter roll" fill={C.green} radius={[3, 3, 0, 0]} maxBarSize={28} />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Panel>
        </Grid>
      </Grid>

      <Panel title={`Summary · ${scopeLabel}, ${period}`} sx={{ height: 'auto' }}>
        <DataTable rows={rows} columns={columns} rowKey={(r) => r.key} maxHeight={560} fileName="rri-counties"
          searchPlaceholder="Search county…" emptyText={loading ? 'Loading…' : 'No RRI returns yet'} />
      </Panel>
      <Typography sx={{ fontSize: '0.68rem', color: C.muted, mt: 1.5 }}>
        RRI figures are the daily county returns uploaded to the RRI USAJILI dashboard. Voter-roll figures are from the IEBC roll for the same
        counties: "On the roll, RRI period" counts voters whose registration date falls in the RRI period.
      </Typography>
    </Section>
  );
};
